import { Callout, Flex, Text } from '@radix-ui/themes'
import { PropsWithChildren, ReactNode } from 'react'
import { format } from 'date-fns/format'
import {
  CalendarIcon,
  CarIcon,
  CircleIcon,
  EqualSquareIcon,
  FilePlusIcon,
  MinusCircleIcon,
  MinusSquareIcon,
  PlusCircleIcon,
  PlusSquareIcon,
} from 'lucide-react'

import { getTotalMargin } from '../../utils/margin-report-helpers'

import { formatWithThousandSep } from '@/utils/utils'
import Margin from '@/interfaces/Margin'

interface Props {
  withAdditionals?: boolean
  margins: Margin[]
  filters: {
    since: Date
    until: Date
  }
}

interface MarginCalloutProps extends Pick<Callout.RootProps, 'color'>, PropsWithChildren {
  icon: ReactNode
  label: string
}

const MarginCallout = ({ color, icon, children, label }: MarginCalloutProps) => (
  <Callout.Root color={color}>
    <Callout.Icon>{icon}</Callout.Icon>
    <Callout.Text align="right">
      {children}
      {label && (
        <>
          <br />
          <Text size="1">{label}</Text>
        </>
      )}
    </Callout.Text>
  </Callout.Root>
)

export const MarginCallouts = ({ margins, withAdditionals = true, filters: { since, until } }: Props) => {
  const sums = margins.reduce(
    (acc, m) => {
      acc.additionals += Number(m.additionalTotal ?? 0)
      // FIXED: Use finalCost to match "Compra" column in table
      acc.purchases += Number(m.finalCost ?? 0)
      // FIXED: Use settlementTotal to match "Liquidación" column in table
      acc.settlements += Number(m.settlementTotal ?? 0)
      acc.expenses += Number(m.expensesTotal ?? 0)
      acc.credits += Number(m.creditFee ?? 0) // Added for credits sum
      acc.otherIncome += Number(m.incomesTotal ?? 0) // Added for other income sum
      acc.sales += Number(m.salenoteTotal ?? 0)
      // `m.margin` is the vehicle margin only; the total adds the credit commission
      // on top, mirroring the "Margen Vehículo" / "Margen Total" table columns.
      acc.vehicleMargin += Number(m.margin ?? 0)
      acc.margin += getTotalMargin(m)

      // UPDATED: Ventas box should follow NEW business logic
      // NEW Business Logic: revenue = (sale value + adicionales + credit commission + other income) or just sale value
      if (withAdditionals) {
        acc.revenue +=
          Number(m.price ?? 0) + Number(m.additionalTotal ?? 0) + Number(m.creditFee ?? 0) + Number(m.incomesTotal ?? 0)
      } else {
        acc.revenue += Number(m.price ?? 0) // Only sale value when additionals are excluded
      }

      return acc
    },
    {
      additionals: 0,
      purchases: 0,
      settlements: 0, // New field for liquidaciones
      expenses: 0,
      credits: 0, // New field for credits
      otherIncome: 0, // New field for other income
      vehicleMargin: 0, // Margin before the credit commission
      margin: 0, // Total margin (vehicle margin + credit commission)
      sales: 0,
      revenue: 0, // Updated field for new revenue calculation
    }
  )

  const getCalloutColor = (): Callout.RootProps['color'] => {
    if (sums.margin < 0) {
      return 'red'
    }

    if (sums.margin > 0) {
      return 'green'
    }

    return 'gray'
  }

  const getCalloutIcon = () => {
    if (sums.margin < 0) {
      return <MinusCircleIcon />
    }

    if (sums.margin > 0) {
      return <PlusCircleIcon />
    }

    return <CircleIcon />
  }

  const formatDate = (date: Date) => format(date, 'PP')

  return (
    <Flex gap="2" wrap="wrap">
      <MarginCallout label="Período" color="gray" icon={<CalendarIcon />}>
        <Text weight="bold">{formatDate(since)}</Text>
        {' a '}
        <Text size="2" weight="bold">
          {formatDate(until)}
        </Text>
      </MarginCallout>

      <MarginCallout label="Vehículos" color="cyan" icon={<CarIcon />}>
        <Text weight="bold">{margins.length}</Text>
      </MarginCallout>

      <MarginCallout label="Ventas" color="teal" icon={<PlusSquareIcon />}>
        <Text weight="bold">${formatWithThousandSep(sums.revenue)}</Text>
      </MarginCallout>

      <MarginCallout label="Costo Vehículos (Compras+Liquidaciones)" color="pink" icon={<MinusSquareIcon />}>
        <Text weight="bold">${formatWithThousandSep(sums.purchases + sums.settlements)}</Text>
      </MarginCallout>

      <MarginCallout label="Gastos" color="purple" icon={<MinusSquareIcon />}>
        <Text weight="bold">${formatWithThousandSep(sums.expenses)}</Text>
      </MarginCallout>

      <MarginCallout label="Costos Vehículos + Gastos" color="violet" icon={<EqualSquareIcon />}>
        <Text weight="bold">${formatWithThousandSep(sums.purchases + sums.settlements + sums.expenses)}</Text>
      </MarginCallout>

      {withAdditionals && (
        <MarginCallout label="Adicionales" color="blue" icon={<FilePlusIcon />}>
          <Text weight="bold">${formatWithThousandSep(sums.additionals)}</Text>
        </MarginCallout>
      )}

      <MarginCallout label="Margen Vehículo" color="jade" icon={<EqualSquareIcon />}>
        <Text weight="bold">${formatWithThousandSep(sums.vehicleMargin)}</Text>
      </MarginCallout>

      <MarginCallout label="Créditos" color="orange" icon={<PlusSquareIcon />}>
        <Text weight="bold">${formatWithThousandSep(sums.credits)}</Text>
      </MarginCallout>

      <MarginCallout label="Margen Total" color={getCalloutColor()} icon={getCalloutIcon()}>
        <Text weight="bold">${formatWithThousandSep(sums.margin)}</Text>
      </MarginCallout>
    </Flex>
  )
}
